// The playground — the library's proof surface. One thread, every fixture
// under every look under both renderers, with the boundaries overlay and the
// agent-log pane a click away. It came out of the lona app's
// `/dev/assets/chat` page (tento-chat-js plan, revision 2, step 5): the page
// keeps its route and now MOUNTS this element, handing it the product's
// transport, the product's block plugins and the rich renderers. Without a
// host the playground still works — a local simulator answers the composer,
// so a storybook, a review card or a static page can drive the whole matrix
// with no server behind it. The dataset × look matrix caught three defects
// the first time it ran; it belongs with the code it tests.
import "../chat/chat-thread";
import "../chat/chat-composer";
import "../chat/chat-debug-view";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Dropdown } from "@tento-ui/components/controls/dropdown";
import { DateTime } from "@tento-chrono";
import { ChatThread } from "../chat/chat-thread";
import { ChatComposer } from "../chat/chat-composer";
import { ChatDebugView } from "../chat/chat-debug-view";
import type { ChatBlockPlugin } from "../chat/chat-block-plugin";
import type { ChatTheme } from "../chat/chat-theme";
import type { StreamDelta } from "../chat/chat-types";
import type { AgentStreamDelta, ChatMessage, PipelineDebug } from "../model/chat-types";
import { Renderers } from "../render/prose";
import { ChatLooks } from "./looks";

let nextId = 1;
function makeId(): string {
  return "msg-" + nextId++;
}

/** The library's datasets: tsh harness turns and nothing invented. Loaded
 * lazily — the full replay is a 33k-line module, and a page that never opens
 * the picker never pays for it. */
const LIBRARY_DATASETS: ChatPlayground.DataSet[] = [
  { label: "legacy-replay · 14925", load: async () => (await import("../fixtures/chat-fixtures")).ChatFixtures.full() },
  { label: "Units 274–318", load: async () => (await import("../fixtures/chat-fixtures")).ChatFixtures.session() },
  { label: "Unit 285", load: async () => (await import("../fixtures/chat-fixtures")).ChatFixtures.unit285() },
  { label: "Rendered (canonical)", load: async () => (await import("../fixtures/chat-fixtures")).ChatFixtures.rendered() },
];

/** What the simulator answers with when no transport is bound: a reply that
 * exercises every construct the prose seam renders, so the Rich/Plain
 * toggle has something to show. */
const SIMULATED_REPLY = [
  "## What I did",
  "",
  "You said: *{text}*. This reply is **simulated** — no transport is bound to the playground — and streams through the same `applyDelta` path a live agent uses.",
  "",
  "| Construct | Plain | Rich |",
  "| --- | --- | --- |",
  "| headings, lists, emphasis | yes | yes |",
  "| pipe tables | yes | via `Documentation.serde` |",
  "| nested lists, HTML blocks | flat / literal | yes |",
  "",
  "```ts",
  "const renderers = Renderers.current();",
  "await renderers.prose.render($into, markdown);",
  "```",
  "",
  "> A quote, and a [link](https://lona.so) — both render on either path.",
].join("\n");

@component({ name: "std-chat-playground" })
export class ChatPlayground extends LonaWebComponent {
  private $thread: ChatThread = this.hydrate("thread");
  private $composer: ChatComposer = this.hydrate("composer");
  private $dataSelect: Dropdown<number> = this.hydrate("data-select");
  private $lookSelect: Dropdown<number> = this.hydrate("look-select");
  private $richChip: HTMLElement = this.$("rich-chip");
  private $plainChip: HTMLElement = this.$("plain-chip");
  private $boundariesChip: HTMLElement = this.$("boundaries-chip");
  private $logsChip: HTMLElement = this.$("logs-chip");
  private $debugView: ChatDebugView = this.hydrate("debug-view");

  private transport: Option<ChatPlayground.Transport> = null;
  private onToolUse: Option<(action: unknown) => void> = null;
  private looks: ChatPlayground.Look[] = ChatLooks.all;
  private datasets: ChatPlayground.DataSet[] = LIBRARY_DATASETS;
  private dataIndex = 0;
  private lookIndex = 0;
  private renderer: "rich" | "plain" = "rich";
  private messages: ChatMessage[] = [];
  private streamingMessageId: Option<string> = null;
  private connection: Option<{ abort(): void }> = null;
  private sessionId: string = crypto.randomUUID();
  private loadGen = 0;

  constructor() {
    super();
    this.$composer.bind({ onSend: (text) => this.sendMessage(text) });
    // The floating composer's height is the host's half of the thread's
    // safe area: measured, not guessed, so the last row always clears the
    // pill by exactly the look's own bottom inset.
    new ResizeObserver(() => {
      this.$thread.style.setProperty("--thread-bottom-occlusion", `${Math.ceil(this.$composer.offsetHeight)}px`);
    }).observe(this.$composer);
    this.wireChips();
  }

  static makeWith(props: ChatPlayground.Props): ChatPlayground {
    return (ChatPlayground.make() as ChatPlayground).bind(props);
  }

  /**
   * A host hands in what is its: a transport (else the simulator answers),
   * the block plugins it wants proven (the lona app passes its sheet /
   * cell / proposal plugins), which of those are transient decorations, a
   * tool-use handler, and optionally its own looks and datasets. Renderers
   * are NOT a prop: the host installs them on the page (`Renderers.install`)
   * and the Rich/Plain chips switch this thread between the page's and the
   * library's plain defaults.
   */
  bind(props: ChatPlayground.Props): this {
    this.transport = props.transport ?? null;
    this.onToolUse = props.onToolUse ?? null;
    for (const plugin of props.plugins ?? []) this.$thread.registerPlugin(plugin);
    if (props.transientBlockTypes) this.$thread.transientBlockTypes = new Set(props.transientBlockTypes);
    if (props.looks) this.looks = props.looks;
    if (props.datasets) this.datasets = props.datasets;
    this.buildPickers();
    void this.$thread.setTheme(this.looks[this.lookIndex]?.theme ?? null);
    void this.loadDataset(this.dataIndex);
    return this;
  }

  private buildPickers(): void {
    this.$dataSelect.bind({
      rows: this.datasets.map((d, i) => ({ content: d.label, data: i, selected: i === this.dataIndex })),
      onSelectedRow: (row) => void this.loadDataset(row.data),
    });
    this.$lookSelect.bind({
      rows: this.looks.map((l, i) => ({ content: l.label, data: i, selected: i === this.lookIndex })),
      onSelectedRow: (row) => this.selectLook(row.data),
    });
  }

  private wireChips(): void {
    GESTURE_MANAGER.addPointerEvent(this.$richChip, { onClick: () => this.selectRenderer("rich") });
    GESTURE_MANAGER.addPointerEvent(this.$plainChip, { onClick: () => this.selectRenderer("plain") });
    GESTURE_MANAGER.addPointerEvent(this.$boundariesChip, {
      onClick: () => {
        const on = !this.$thread.showBoundaries;
        this.$thread.showBoundaries = on;
        this.$thread.refreshBoundaries();
        this.$boundariesChip.classList.toggle("active", on);
      },
    });
    GESTURE_MANAGER.addPointerEvent(this.$logsChip, {
      onClick: () => {
        const on = !this.hasAttribute("logs");
        this.toggleAttribute("logs", on);
        this.$logsChip.classList.toggle("active", on);
      },
    });
    this.$richChip.classList.add("active");
  }

  private async loadDataset(index: number): Promise<void> {
    const gen = ++this.loadGen;
    this.dataIndex = index;
    const messages = await this.datasets[index].load();
    if (gen !== this.loadGen) return; // a later pick won
    this.messages = messages;
    await this.$thread.setMessages(messages);
  }

  private selectLook(index: number): void {
    if (index === this.lookIndex) return;
    this.lookIndex = index;
    void this.$thread.setTheme(this.looks[index].theme);
  }

  /** Rich = whatever the page installed (`Renderers.current()`); Plain = the
   * library's defaults. A full rebind, because rendered blocks are cached
   * per cell and a renderer swap is a shape change. */
  private selectRenderer(mode: "rich" | "plain"): void {
    if (mode === this.renderer) return;
    this.renderer = mode;
    this.$richChip.classList.toggle("active", mode === "rich");
    this.$plainChip.classList.toggle("active", mode === "plain");
    this.$thread.setRenderers(mode === "plain" ? Renderers.plain() : {});
    void this.$thread.setMessages(this.messages);
  }

  private async sendMessage(text: string): Promise<void> {
    const userMsg: ChatMessage = { id: makeId(), role: "user", content: [{ type: "text", text }], isStreaming: false, createdAt: Date.now() };
    await this.$thread.addMessage(userMsg);
    this.messages.push(userMsg);
    const assistantId = makeId();
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: [{ type: "loading" }],
      isStreaming: true,
      createdAt: Date.now(),
      streamStartedAt: DateTime.utc.now(),
    };
    await this.$thread.addMessage(assistantMsg);
    this.messages.push(assistantMsg);
    this.streamingMessageId = assistantId;

    const sink: ChatPlayground.Sink = {
      delta: (delta) => {
        const patched = { ...delta, messageId: assistantId } as StreamDelta;
        this.$thread.applyDelta(patched);
      },
      debug: (debug) => this.$debugView.addEntry({ messageId: this.streamingMessageId ?? "", debug }),
      toolUse: (action) => this.onToolUse?.(action),
      close: () => { this.connection = null; },
      error: (err) => { console.error("chat playground transport:", err); this.connection = null; },
    };
    this.connection?.abort();
    this.connection = this.transport ? this.transport.stream(text, this.sessionId, sink) : ChatPlayground.simulate(text, sink);
  }

  /** The built-in transport: streams a canned markdown reply in word-sized
   * deltas at a typing cadence, through exactly the delta types a live
   * agent emits. */
  static simulate(text: string, sink: ChatPlayground.Sink): { abort(): void } {
    const reply = SIMULATED_REPLY.replace("{text}", text.replace(/\n/g, " "));
    const words = reply.split(/(?<=\s)/);
    let i = 0;
    let timer: Option<number> = null;
    const messageId = "simulated";
    sink.delta({ type: "content_block_start", messageId, blockIndex: 0, block: { type: "text", text: "" } });
    const tick = () => {
      if (i >= words.length) {
        sink.delta({ type: "content_block_stop", messageId, blockIndex: 0 });
        sink.delta({ type: "message_stop", messageId });
        sink.close();
        timer = null;
        return;
      }
      sink.delta({ type: "text_delta", messageId, blockIndex: 0, delta: words[i++] });
      timer = window.setTimeout(tick, 18);
    };
    timer = window.setTimeout(tick, 120);
    return { abort: () => { if (timer != null) window.clearTimeout(timer); timer = null; } };
  }

  static $styles = [
    Typography.$style,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        box-sizing: border-box;
        padding: 2px;
        container-type: inline-size;
      }
      /* One chrome band. Overflow is structurally impossible: the band
         wraps whole GROUPS and min-width:0 runs down the chain, so a
         narrow panel gets a second toolbar line — never a page h-scroll. */
      #toolbar {
        width: 100%;
        box-sizing: border-box;
        min-width: 0;
        padding: 8px 14px;
        flex-shrink: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 8px 16px;
        border-bottom: 1px solid var(--divider);
      }
      .group { display: flex; align-items: center; gap: 8px; min-width: 0; }
      .field { flex-direction: column; align-items: stretch; gap: 3px; }
      .field .caption { padding-inline-start: 21px; }
      #toolbar p { --margin-top: 0px; --margin-bottom: 0px; color: var(--text-sec, #666); flex-shrink: 0; }
      #data-select, #look-select { --dropdown-search-row-height: 20px; --std-icon-button-dimension: 20px; min-width: 0; }
      #data-select { width: 230px; }
      #look-select { width: 170px; }
      #spacer { flex: 1 1 0; }
      .chip {
        display: inline-flex;
        align-items: center;
        box-sizing: border-box;
        height: 30px;
        padding: 0 12px;
        border-radius: 8px;
        border: 1px solid var(--divider);
        background: transparent;
        color: var(--text-sec, #666);
        font: inherit;
        font-size: calc(var(--p-size, 0.625rem) / (1 - var(--cap-height, 0.125) - var(--baseline, 0.155)));
        cursor: pointer;
        white-space: nowrap;
      }
      .chip:hover { background: var(--hover-color, rgba(0, 0, 0, 0.04)); }
      .chip.active { color: var(--bg, #fff); background: var(--text-pri); border-color: var(--text-pri); }
      .segment { display: inline-flex; }
      .segment .chip:first-child { border-radius: 8px 0 0 8px; }
      .segment .chip:last-child { border-radius: 0 8px 8px 0; margin-left: -1px; }
      #main-row { flex: 1; min-height: 0; gap: 2px; }
      #chat-container {
        flex: 3 1 0;
        border-radius: 12px 12px 0 0;
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
        background: var(--bg, #fff);
        overflow: hidden;
        border: 1px solid var(--divider);
        border-bottom: none;
        position: relative;
      }
      /* No padding on the thread: padding is a hard clip. The looks declare
         the vertical breathing room as a safe-area inset the rows scroll
         through, and the composer's height rides the same safe area. */
      #thread { flex: 1; min-height: 0; isolation: isolate; }
      #composer { position: absolute; left: 0; right: 0; bottom: 0; z-index: 1; padding: 12px 16px; }
      #debug-view {
        flex: 2 1 0;
        min-width: 360px;
        border-radius: 12px 12px 0 0;
        background: var(--bg, #fff);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        border: 1px solid var(--divider);
        border-bottom: none;
      }
      :host(:not([logs])) #debug-view { display: none; }
      @container (max-width: 880px) {
        #main-row { flex-direction: column; }
        #chat-container { flex: 3 1 0; }
        :host([logs]) #chat-container { border-bottom: 1px solid var(--divider); border-radius: 12px; }
        #debug-view { min-width: 0; min-height: 0; flex: 2 1 0; overflow-y: auto; }
      }
    `,
  ];

  static $html = html`
    <div id="toolbar">
      <div class="group field">
        <p class="caption">Data</p>
        <std-dropdown id="data-select"></std-dropdown>
      </div>
      <div class="group field">
        <p class="caption">Look</p>
        <std-dropdown id="look-select"></std-dropdown>
      </div>
      <div class="group field">
        <p class="caption">Renderer</p>
        <div class="segment">
          <button id="rich-chip" class="chip" type="button">Rich</button>
          <button id="plain-chip" class="chip" type="button">Plain</button>
        </div>
      </div>
      <div id="spacer"></div>
      <div class="group">
        <button id="boundaries-chip" class="chip" type="button">Boundaries</button>
        <button id="logs-chip" class="chip" type="button">Agent logs</button>
      </div>
    </div>
    <std-row id="main-row">
      <std-col id="chat-container">
        <std-chat-thread id="thread"></std-chat-thread>
        <std-chat-composer id="composer"></std-chat-composer>
      </std-col>
      <std-chat-debug-view id="debug-view"></std-chat-debug-view>
    </std-row>
  `;
}

export namespace ChatPlayground {
  export interface Look {
    label: string;
    theme: ChatTheme;
  }
  export interface DataSet {
    label: string;
    load: () => Promise<ChatMessage[]>;
  }
  /** What a transport feeds back. `messageId` on a delta is ignored — the
   * playground re-keys every delta to the assistant message it created. */
  export interface Sink {
    delta(delta: AgentStreamDelta): void;
    debug(debug: PipelineDebug): void;
    toolUse(action: unknown): void;
    close(): void;
    error(err: unknown): void;
  }
  export interface Transport {
    stream(text: string, sessionId: string, sink: Sink): { abort(): void };
  }
  export type Props = {
    transport?: Transport;
    plugins?: ChatBlockPlugin[];
    transientBlockTypes?: string[];
    onToolUse?: (action: unknown) => void;
    looks?: Look[];
    datasets?: DataSet[];
  };
}
